import React, { useState, forwardRef } from 'react'
import { Select } from '../../../components'
import { startTimeOptions, endTimeOptions } from '../helpers/timeOptions'

// Move an already-booked interview, or fix its place. Booking a round for the first time
// is not possible here — that only happens alongside the milestone that requires it, so
// this form always edits an existing recruitment.candidate_interview row.
//
// Same string-slicing of the stored slot as forms/candidateMilestone.js: the column is a
// timestamp *without* time zone (wall-clock), so parsing it through Date and formatting it
// back would shift it.
const splitSlot = (value) => {
  if (!value) return { date: '', time: '' }
  const s = String(value)
  return { date: s.slice(0, 10), time: s.slice(11, 16) }
}

const _InterviewRescheduleForm = ({ data, submitForm, loading, setLoading }, ref) => {

  const interview = data && data[0]
  const start = splitSlot(interview?.interview_start_timestamp)
  const end   = splitSlot(interview?.interview_end_timestamp)

  const [formData, setFormData] = useState({
    date:     start.date,
    time:     start.time,
    end_time: end.time,
    location: interview?.interview_location || '',
  })
  const [invalid, setInvalid] = useState({})

  // Native inputs report through event.target; the shared <Select> reports {name, value}.
  const handleInputChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event

    const invalidForm = { ...invalid }
    delete invalidForm[name]
    setInvalid(invalidForm)

    const next = { ...formData, [name]: value }
    // Moving the start past the chosen end drops the end — same rule as the booking form.
    if (name === 'time' && next.end_time && next.end_time <= value) next.end_time = ''
    setFormData(next)
  }

  const onSubmit = (event) => {
    event.preventDefault()

    if (!formData.date) {
      setInvalid({ date: { message: 'Välj ett datum för intervjun' } })
      return
    }
    if (!formData.time) {
      setInvalid({ time: { message: 'Välj en starttid för intervjun' } })
      return
    }

    setLoading(true)
    submitForm({
      start_timestamp: `${formData.date}T${formData.time}:00`,
      // Explicitly null rather than omitted when the field is cleared — omitting it means
      // "leave it alone", and the API would then shift the old end to keep the duration.
      end_timestamp:   formData.end_time ? `${formData.date}T${formData.end_time}:00` : null,
      location:        formData.location.trim() || null,
    })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>

        <p className="cd-form-hint">
          Intervju {interview?.interview_round} · {interview?.candidate_name}. Anmälda kollegor följer med till den nya tiden.
        </p>

        {/* Same layout as the booking half of forms/candidateMilestone.js — keep the two
            in step, they are the same three fields. */}
        <div className="form-block vertical">
          <label>Datum<span className="label-required">*</span></label>
          <input className="cd-plain-input cd-slot-date" type="date" name="date" value={formData.date} onChange={handleInputChange} disabled={loading} />
        </div>

        {/* Selects directly in .form-row with their own `label` — see the note in
            forms/candidateMilestone.js for why a .form-block wrapper breaks this. */}
        <div className="form-row">
          <Select
            name="time"
            label={<>Från <span className="label-required">*</span></>}
            value={formData.time}
            options={startTimeOptions(formData.time)}
            onChange={handleInputChange}
            disabled={loading}
            invalid={invalid.time}
            placeholder="Välj tid…"
          />
          <Select
            name="end_time"
            label={<>Till <span className="cd-slot-hint">valfritt</span></>}
            value={formData.end_time}
            options={endTimeOptions(formData.time, formData.end_time)}
            onChange={handleInputChange}
            disabled={loading}
            isNullable
            placeholder="—"
          />
        </div>

        <div className="form-block vertical">
          <label>Plats</label>
          <input
            className="cd-plain-input"
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Rum, adress eller möteslänk"
          />
        </div>

        {Object.keys(invalid).length > 0 &&
          <div className="form-block form-error">
            {Object.entries(invalid).map(([key, value]) => (<p key={key}>{value.message}</p>))}
          </div>
        }
      </form>
    </div>
  )
}

export const InterviewRescheduleForm = forwardRef(_InterviewRescheduleForm)
